import { Constants } from "Shared/Constants";
import { useAppSettings } from "Shared/Hooks";

interface flightObject {
    envs?: string[];
}

/*
 * This is a hook that is used to get the environment.
 */
export const useEnvironment = () => {
    const currentEnvironment = useAppSettings().NODE_ENV?.toLocaleLowerCase() ?? "";
    const isAirGap = currentEnvironment.startsWith("ag");
    const isSovereign = Constants.sovereignOnlyEnvs.some((env) => env.toLowerCase() === currentEnvironment);
    const isSovereignBleu = Constants.sovereignBleuOnlyEnv.some((env) => env.toLowerCase() === currentEnvironment);
    const isSovereignDelos = Constants.sovereignDelosOnlyEnv.some((env) => env.toLowerCase() === currentEnvironment);
    const isDev = Constants.devOnlyEnvs.some((env) => env.toLowerCase() === currentEnvironment);
    const isPPE = Constants.ppeOnlyEnvs.some((env) => env.toLowerCase() === currentEnvironment);
    const isPpeOrProd = Constants.ppeAndProdOnlyEnvs.some((env) => env.toLowerCase() === currentEnvironment);
    const isPublic = Constants.prodOnlyEnvs.some((env) => env.toLowerCase() === currentEnvironment);
    const filterByEnvironment = <T extends flightObject>(objects: T[]) =>
        objects.filter((obj) => !obj.envs || obj.envs.map((env) => env.toLowerCase()).includes(currentEnvironment));

    return {
        /** The current environment string in lowercase. E.g., "development", "prod", "ag08", "ppe", "bleu", "delos"
         */
        currentEnvironment,
        /** Indicates if the current environment is an air-gapped environment (AG) including AG08 and AG09.
         */
        isAirGap,
        /**
         * Indicates if the current environment is any of sovereign cloud environment including Bleu or Delos.
         */
        isSovereign,
        /**
         * Indicates if the current environment is only Bleu environment.
         */
        isSovereignBleu,
        /**
         * Indicates if the current environment is only Delos environment.
         */
        isSovereignDelos,
        /** Indicates if the current environment is a development environment.
         */
        isDev,
        /** Indicates if the current environment is a PPE environment.
         */
        isPPE,
        /** Indicates if the current environment is either PPE or Prod.
         */
        isPpeOrProd,
        /** Indicates if the current environment is a public environment (Prod).
         */
        isPublic,
        /** Filters an array of objects based on the current environment.
         * Each object can optionally have an 'envs' property which is an array of environment strings.
         * If 'envs' is not present, the object is included by default.
         * If 'envs' is present, the object is included only if the current environment is in that array.
         */
        filterByEnvironment
    };
};
